import React from 'react';
import {
    makeStyles,
    tokens,
    Card,
    Accordion,
    AccordionHeader,
    AccordionItem,
    AccordionPanel,
    Subtitle2
} from '@fluentui/react-components';
import { MediaEntry } from './MediaEntry';
import { UserMedia } from '../../types';

const useStyles = makeStyles({
    listContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalM
    },
    gridContainer: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', // Smaller minimum size
        gap: tokens.spacingHorizontalXXS, // Minimum gap
        padding: tokens.spacingHorizontalXXS,
        justifyItems: 'center',
        '@media (min-width: 460px)': {
            gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
            gap: tokens.spacingHorizontalXS,
            padding: tokens.spacingHorizontalXS,
        },
        '@media (min-width: 768px)': {
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
            gap: tokens.spacingHorizontalS,
        },
        '@media (min-width: 1024px)': {
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            gap: tokens.spacingHorizontalM,
        },
    },
    accordionHeader: {
        position: 'sticky',
        backgroundColor: tokens.colorNeutralBackground1,
        zIndex: 1,
        borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
        marginTop: 0,
        padding: tokens.spacingVerticalXS,
        '@media (min-width: 460px)': {
            top: '76px',
            padding: tokens.spacingVerticalS,
        },
        '@media (min-width: 768px)': {
            top: '84px',
            padding: tokens.spacingVerticalM,
        },
    },
    accordionPanel: {
        paddingTop: tokens.spacingVerticalXXS, // Minimum padding
        '@media (min-width: 460px)': {
            paddingTop: tokens.spacingVerticalXS,
        },
    },
});

interface MediaListProps {
    userMediaList: Array<UserMedia>;
}

const groupBy = <T, K extends keyof any>(arr: T[], key: (i: T) => K) =>
    arr.reduce((groups, item) => {
        (groups[key(item)] ||= []).push(item);
        return groups;
    }, {} as Record<K, T[]>);

const getSortTitle = (entry: UserMedia) =>
    entry.title.english?.trim() || entry.title.romaji?.trim() || '';

export const MediaList: React.FC<MediaListProps> = ({ userMediaList }) => {
    const styles = useStyles();
    const groupedMedia = groupBy(userMediaList, i => i.userStatus);
    const mediaGroups = Object.entries(groupedMedia).map(([key, entries]) => ({
        name: key,
        order: ['CURRENT', 'PLANNING', 'COMPLETED', 'DROPPED', 'PAUSED'].indexOf(key),
        entries: entries.slice().sort((a, b) =>
            getSortTitle(a).localeCompare(getSortTitle(b))
        ),
    }));

    return (
        <div className={styles.listContainer}>
            <Accordion collapsible multiple>
                {mediaGroups.slice().sort((a,b) => a.order > b.order ? 1 : -1).map((group) => (
                    <AccordionItem key={group.name} value={group.name || 'unnamed'}>
                        <AccordionHeader className={styles.accordionHeader}>
                            <Subtitle2>{group.name || 'Unnamed List'}</Subtitle2>
                        </AccordionHeader>
                        <AccordionPanel className={styles.accordionPanel}>
                            <Card>
                                <div className={styles.gridContainer}>
                                    {group.entries.map((entry) => (
                                        <MediaEntry key={entry.id} entry={entry} />
                                    ))}
                                </div>
                            </Card>
                        </AccordionPanel>
                    </AccordionItem>
                ))}
            </Accordion>
        </div>
    );
};
